// Duplicate File Tool - Provides file and directory copy/duplicate functions for workspace
// Extends WorkspaceTool - has access to appType and toolSystem
// Uses ReadFileTool and WriteFileTool internally via tool system

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class DuplicateFileTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'duplicateFileTool';
  }
  /**
   * Duplicate (copy) a file or directory
   * @param {string} sourcePath - Path to the file or directory to duplicate
   * @param {string} destinationPath - Destination path for the copy
   * @param {boolean} [overwrite=false] - Whether to overwrite existing destination
   * @returns {Promise<Object>} Result with operation status
   */
  async duplicateFile(sourcePath, destinationPath, overwrite = false) {
    try {
      // Validate source path
      if (!sourcePath || typeof sourcePath !== "string") {
        return this.returnError({}, "Valid source path required");
      }

      // Validate destination path
      if (!destinationPath || typeof destinationPath !== "string") {
        return this.returnError({}, "Valid destination path required");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.duplicateFileForBrowser(
          sourcePath,
          destinationPath,
          overwrite
        );
      }
      if (this.appType === "local") {
        return await this.duplicateFileForLocal(
          sourcePath,
          destinationPath,
          overwrite
        );
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for duplicateFile`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|duplicatefile|error",
          color1: "red",
        },
        `Failed to duplicate: ${sourcePath} -> ${destinationPath}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Duplicate file or directory for browser apps using Lightning FS
   */
  async duplicateFileForBrowser(sourcePath, destinationPath, overwrite) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Check if ReadFileTool and WriteFileTool are available
    if (!this.toolSystem?.readFileTool) {
      return this.returnError(
        {},
        "ReadFileTool is not available in the tool system"
      );
    }

    if (!this.toolSystem?.writeFileTool) {
      return this.returnError(
        {},
        "WriteFileTool is not available in the tool system"
      );
    }

    // Normalize paths - Lightning FS uses absolute paths
    const normalizedSourcePath = sourcePath.startsWith("/")
      ? sourcePath
      : `/${sourcePath}`;
    const normalizedDestPath = destinationPath.startsWith("/")
      ? destinationPath
      : `/${destinationPath}`;

    // Prepend /files/ to the filesystem paths
    const sourceFilesystemPath = `/files${normalizedSourcePath}`;
    const destFilesystemPath = `/files${normalizedDestPath}`;

    try {
      // Check if source exists
      const sourceStats = await fs.stat(sourceFilesystemPath);
      const sourceIsDir =
        typeof sourceStats.isDirectory === "function"
          ? sourceStats.isDirectory()
          : sourceStats.type === "dir";

      // Check if destination exists
      let destExists = false;
      let destIsDir = false;
      try {
        const destStats = await fs.stat(destFilesystemPath);
        destExists = true;
        destIsDir =
          typeof destStats.isDirectory === "function"
            ? destStats.isDirectory()
            : destStats.type === "dir";
      } catch (error) {
        if (error.code !== "ENOENT") {
          throw error;
        }
        // Destination doesn't exist, which is fine
      }

      // Validate copy operation
      if (destExists) {
        // Destination exists - check if types match
        if (sourceIsDir !== destIsDir) {
          return this.returnError(
            {},
            `Cannot copy ${sourceIsDir ? "directory" : "file"} to ${destIsDir ? "directory" : "file"} - types must match`
          );
        }

        // Check overwrite permission
        if (!overwrite) {
          return this.returnError(
            {},
            `Destination already exists: ${destinationPath}. Use overwrite=true to overwrite`
          );
        }
      }

      if (sourceIsDir) {
        // Copy directory recursively
        return await this.duplicateDirectory(
          sourceFilesystemPath,
          normalizedSourcePath,
          destFilesystemPath,
          normalizedDestPath,
          overwrite
        );
      } else {
        // Copy file
        return await this.duplicateSingleFile(
          normalizedSourcePath,
          normalizedDestPath,
          overwrite
        );
      }
    } catch (error) {
      if (error.code === "ENOENT") {
        return this.returnError({}, `Source not found: ${sourcePath}`);
      }
      throw error;
    }
  }

  /**
   * Duplicate for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async duplicateFileForLocal(sourcePath, destinationPath, overwrite) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.duplicatePath) {
      return this.returnError(
        {},
        "Local duplicate requires Revolt Desktop (workspaceLocalAppAPI.duplicatePath)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedSourcePath = this.normalizeAndValidatePath(sourcePath);
    const normalizedDestPath = this.normalizeAndValidatePath(destinationPath);
    if (!normalizedSourcePath || !normalizedDestPath) {
      return this.returnError({}, "Invalid source or destination path");
    }

    const sourceRelativePath = normalizedSourcePath.replace(/^\/+/, "");
    const destRelativePath = normalizedDestPath.replace(/^\/+/, "");
    if (!sourceRelativePath || !destRelativePath) {
      return this.returnError({}, "Invalid source or destination path");
    }

    const res = await api.duplicatePath({
      rootPath: root,
      sourceRelativePath,
      destRelativePath,
      overwrite,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to duplicate on disk");
    }

    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedDestPath]);
    }

    const type = res.type === "directory" ? "directory" : "file";

    logger.log(
      {
        tags: "tools|duplicatefile|info",
        color1: "lime",
      },
      `Duplicated ${type} (local): ${normalizedSourcePath} -> ${normalizedDestPath}`,
      { source: normalizedSourcePath, destination: normalizedDestPath, overwrite }
    );

    return this.returnSuccess(
      {
        source: normalizedSourcePath,
        destination: normalizedDestPath,
        type,
        overwrite,
        copiedCount: type === "directory" ? undefined : 1,
        errorCount: 0,
      },
      type === "directory"
        ? "Directory duplicated successfully"
        : "File duplicated successfully"
    );
  }

  /**
   * Duplicate a single file
   */
  async duplicateSingleFile(sourcePath, destPath, overwrite) {
    // Read the source file
    const readResult = await this.toolSystem.readFileTool.readFile(
      sourcePath,
      "utf8"
    );

    if (!readResult.success) {
      return readResult;
    }

    // Write to destination
    const writeResult = await this.toolSystem.writeFileTool.writeFile(
      destPath,
      readResult.data.content,
      overwrite,
      "utf8",
      true // createDirectories
    );

    if (!writeResult.success) {
      return writeResult;
    }

    // Sync changes with workspace manager to update UI
    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([destPath]);
    }

    logger.log(
      {
        tags: "tools|duplicatefile|info",
        color1: "lime",
      },
      `Duplicated file: ${sourcePath} -> ${destPath}`,
      {
        source: sourcePath,
        destination: destPath,
        overwrite,
      }
    );

    return this.returnSuccess(
      {
        source: sourcePath,
        destination: destPath,
        type: "file",
        overwrite,
      },
      "File duplicated successfully"
    );
  }

  /**
   * Duplicate a directory recursively
   */
  async duplicateDirectory(
    sourceFilesystemPath,
    normalizedSourcePath,
    destFilesystemPath,
    normalizedDestPath,
    overwrite
  ) {
    const fs = this.fs?.promises;
    let copiedCount = 0;
    let errorCount = 0;

    try {
      // Create destination directory if it doesn't exist
      try {
        await fs.stat(destFilesystemPath);
        // Directory exists - delete it if overwrite is enabled
        if (overwrite) {
          await this.deleteDirectoryRecursive(destFilesystemPath, fs);
          await fs.mkdir(destFilesystemPath);
        }
      } catch (error) {
        if (error.code === "ENOENT") {
          // Directory doesn't exist, create it
          await this.ensureDirectory(
            destFilesystemPath.split("/").slice(0, -1).join("/") || "/",
            fs
          );
          await fs.mkdir(destFilesystemPath);
        } else {
          throw error;
        }
      }

      // Recursively copy directory contents
      await this.copyDirectoryContents(
        sourceFilesystemPath,
        normalizedSourcePath,
        destFilesystemPath,
        normalizedDestPath,
        overwrite,
        fs,
        (success) => {
          if (success) {
            copiedCount++;
          } else {
            errorCount++;
          }
        }
      );

      // Sync changes with workspace manager to update UI
      if (this.workspaceManager?.syncOpenFiles) {
        await this.workspaceManager.syncOpenFiles([normalizedDestPath]);
      }

      logger.log(
        {
          tags: "tools|duplicatefile|info",
          color1: "lime",
        },
        `Duplicated directory: ${normalizedSourcePath} -> ${normalizedDestPath}`,
        {
          source: normalizedSourcePath,
          destination: normalizedDestPath,
          copiedCount,
          errorCount,
          overwrite,
        }
      );

      return this.returnSuccess(
        {
          source: normalizedSourcePath,
          destination: normalizedDestPath,
          type: "directory",
          copiedCount,
          errorCount,
          overwrite,
        },
        `Directory duplicated successfully (${copiedCount} item(s) copied${errorCount > 0 ? `, ${errorCount} error(s)` : ""})`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|duplicatefile|directory",
          color1: "red",
        },
        `Failed to duplicate directory: ${normalizedSourcePath}`,
        error
      );
      throw error;
    }
  }

  /**
   * Recursively copy directory contents
   */
  async copyDirectoryContents(
    sourceFilesystemPath,
    normalizedSourcePath,
    destFilesystemPath,
    normalizedDestPath,
    overwrite,
    fs,
    onItemCopied
  ) {
    try {
      const items = await fs.readdir(sourceFilesystemPath);

      for (const item of items) {
        const sourceItemPath =
          sourceFilesystemPath === "/files"
            ? `/files/${item}`
            : `${sourceFilesystemPath}/${item}`;
        const destItemPath =
          destFilesystemPath === "/files"
            ? `/files/${item}`
            : `${destFilesystemPath}/${item}`;

        const sourceNormalizedPath =
          normalizedSourcePath === "/"
            ? `/${item}`
            : `${normalizedSourcePath}/${item}`;
        const destNormalizedPath =
          normalizedDestPath === "/"
            ? `/${item}`
            : `${normalizedDestPath}/${item}`;

        try {
          const stats = await fs.stat(sourceItemPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          if (isDir) {
            // Create destination directory
            try {
              await fs.mkdir(destItemPath);
            } catch (error) {
              if (error.code !== "EEXIST") {
                throw error;
              }
            }

            // Recursively copy subdirectory
            await this.copyDirectoryContents(
              sourceItemPath,
              sourceNormalizedPath,
              destItemPath,
              destNormalizedPath,
              overwrite,
              fs,
              onItemCopied
            );
            onItemCopied(true);
          } else {
            // Copy file
            const fileResult = await this.duplicateSingleFile(
              sourceNormalizedPath,
              destNormalizedPath,
              overwrite
            );
            onItemCopied(fileResult.success);
          }
        } catch (error) {
          logger.warn(
            {
              tags: "tools|duplicatefile|entry",
              color1: "yellow",
            },
            `Failed to copy entry: ${sourceItemPath}`,
            error
          );
          onItemCopied(false);
          // Continue with other entries even if one fails
        }
      }
    } catch (error) {
      logger.error(
        {
          tags: "tools|duplicatefile|read",
          color1: "red",
        },
        `Failed to read directory during copy: ${sourceFilesystemPath}`,
        error
      );
      throw error;
    }
  }

  /**
   * Recursively delete a directory and all its contents
   */
  async deleteDirectoryRecursive(dirPath, fs) {
    try {
      const items = await fs.readdir(dirPath);

      for (const item of items) {
        const itemPath =
          dirPath === "/files" ? `/files/${item}` : `${dirPath}/${item}`;

        try {
          const stats = await fs.stat(itemPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          if (isDir) {
            await this.deleteDirectoryRecursive(itemPath, fs);
            await fs.rmdir(itemPath);
          } else {
            await fs.unlink(itemPath);
          }
        } catch (error) {
          // Continue even if one item fails
        }
      }

      await fs.rmdir(dirPath);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Ensure directory exists, creating parent directories as needed
   */
  async ensureDirectory(directoryPath, fs) {
    if (!directoryPath || directoryPath === "/" || directoryPath === "/files") {
      return;
    }

    const segments = directoryPath.split("/").filter(Boolean);
    let currentPath = "";

    for (const segment of segments) {
      currentPath += `/${segment}`;
      try {
        await fs.stat(currentPath);
      } catch (error) {
        if (error.code === "ENOENT") {
          try {
            await fs.mkdir(currentPath);
          } catch (mkdirError) {
            if (mkdirError.code !== "EEXIST") {
              logger.warn(
                {
                  tags: "tools|duplicatefile|mkdir",
                  color1: "yellow",
                },
                `Failed to create directory: ${currentPath}`,
                mkdirError
              );
            }
          }
        } else {
          throw error;
        }
      }
    }
  }
}
